import { useState } from "react";
import { centsToDollarsFormatted, currencyFormatter } from "@/lib/currencyHelpers";
import TextField from "@/components/TextField";
import { Controller, useFormContext } from "react-hook-form";
import Checkbox from "@/components/Checkbox";
import { NumberFormatBase } from "react-number-format";
import Icon from "@/components/Icon";
import { FormValues } from "../index";
import Select from "@/components/Select";
import Divider from "@/components/Divider";
import { PaymentStatus } from "@/pages/billing/types";

export default function RefundAmount({
  maxMonths,
  paymentStatus
}: {
  maxMonths: number;
  paymentStatus: PaymentStatus;
}) {
  const { control, watch, setValue } = useFormContext<FormValues>();
  const refundAllocations = watch("refundAllocations");
  const [isManualAmountChange, setIsManualAmountChange] = useState(false);

  return (
    <>
      <p className="text-body-medium text-sys-brand-on-surface">
        Set the refund amount per product. The refund amount cannot be more than the remaining net payment for each
        product.
      </p>
      <div className="flex py-3 items-center bg-sys-brand-secondary-container rounded-2xl mt-6 mb-4">
        <div className="px-4 ">
          <Icon name="info" />
        </div>
        <div className="px-6 border-l border-sys-brand-outline-variant">
          <h6 className="text-title-small text-sys-brand-on-seconday-container">Want to Make Product Changes?</h6>
          <p className="text-body-medium text-sys-brand-on-seconday-container">
            Issuing a refund on this payment will keep the order as-is. Changes to the order can be made by editing the
            order.
          </p>
        </div>
      </div>
      {refundAllocations.some((item) => item.proratable && paymentStatus === PaymentStatus.PAID) && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full py-2">
            <div className="flex-1">
              <div className="flex justify-between items-center my-2 w-full">
                <div className="flex flex-shrink-0 w-10 h-10 rounded-[6px] bg-extended-pgasilver-brand-color items-center justify-center mr-4">
                  <Icon name="calculate" size={24} className="text-extended-pgasilver-brand-on-color" />
                </div>
                <div className="flex flex-col items-start">
                  <div className="break-words text-sys-brand-on-surface text-body-large-semibold">
                    Prorate Refund by Months
                  </div>

                  <div className="text-sys-brand-on-surface-variant text-body-medium">
                    Choose the Months to Refund to auto-calculate refund amounts for applicable products.
                  </div>
                </div>
              </div>
            </div>
            <div className="w-[210px] mt-5 sm:mt-0 ml-8 sm:ml-2">
              <Controller
                control={control}
                name="refundMonths"
                render={({ field }) => (
                  <Select
                    options={Array.from({ length: maxMonths }).map((_, index) => ({
                      label: `${index + 1}`,
                      value: `${index + 1}`
                    }))}
                    onSelect={(value) => {
                      field.onChange(value);
                      const newRefundAllocations = refundAllocations.map((item) => ({
                        ...item,
                        selected: item.proratable,
                        amountCents: item.proratable ? item.defaulAmountCents * (Number(value) / maxMonths) : 0
                      }));
                      setValue("refundAllocations", newRefundAllocations);
                    }}
                    value={field.value?.toString()}
                    label="Months to Refund"
                    dropdownHeight="short"
                  />
                )}
              />
            </div>
          </div>
          <div className="py-3">
            <Divider />
          </div>
        </>
      )}
      <div className="">
        {refundAllocations?.map(
          (item, index) =>
            item.defaulAmountCents !== 0 && (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full py-2"
              >
                <div className="flex items-center">
                  <input type="hidden" name={`refundAllocations.${index}.id`} />
                  <Controller
                    render={({ field }) => {
                      return (
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e);
                            if (e === false) {
                              setValue(`refundAllocations.${index}.amountCents`, 0);
                            } else {
                              if (watch("refundMonths") && item.proratable) {
                                setValue(
                                  `refundAllocations.${index}.amountCents`,
                                  item.defaulAmountCents * (Number(watch("refundMonths")) / maxMonths)
                                );
                              } else {
                                setValue(`refundAllocations.${index}.amountCents`, item.defaulAmountCents);
                              }
                            }
                          }}
                          name={`refunds.${index}.selected`}
                        />
                      );
                    }}
                    control={control}
                    name={`refundAllocations.${index}.selected`}
                  />
                  <div className="ml-4">
                    <div className="text-body-large text-sys-brand-on-surface text-nowrap">{item.name}</div>
                    <div className="text-body-medium text-sys-brand-on-surface-variant">
                      Net Payment {centsToDollarsFormatted(item.defaulAmountCents)}
                    </div>
                  </div>
                </div>
                <div className="w-[210px] mt-5 sm:mt-0 ml-8 sm:ml-0">
                  <Controller
                    render={({ field, fieldState }) => (
                      <NumberFormatBase
                        disabled={!item.selected}
                        value={field.value}
                        format={currencyFormatter}
                        customInput={TextField}
                        error={!!fieldState.error}
                        errorText={fieldState.error?.message}
                        onFocus={(e) => {
                          e.target.select();
                          setIsManualAmountChange(true);
                        }}
                        onBlur={() => {
                          setIsManualAmountChange(false);
                        }}
                        onValueChange={(values) => {
                          field.onChange(Math.round(values.floatValue || 0));

                          if (isManualAmountChange) {
                            setValue("refundMonths", null as any);
                            setIsManualAmountChange(false);
                          }
                        }}
                        leadingIcon="attach_money"
                        label="Refund Amount"
                      />
                    )}
                    control={control}
                    name={`refundAllocations.${index}.amountCents`}
                    rules={{
                      max: {
                        value: item.defaulAmountCents,
                        message: "Must be less than Net Payment"
                      }
                    }}
                  />
                </div>
              </div>
            )
        )}
      </div>
    </>
  );
}
